import React, { useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import useVideoEditTagSection from './useVideoEditTagSection';
import BulkTagSettingDialog from './BulkTagSettingDialog';
import { VideoEditTagSectionProps, TagBadgeProps, BodyPart } from './types';
import styles from './VideoEditTagSection.module.css';

/**
 * タグバッジコンポーネント
 * 
 * @param props - タグバッジのプロパティ
 * @param props.tag - 表示するタグのテキスト
 * @param props.index - タグの配列内のインデックス
 * @returns タグバッジのReactコンポーネント
 */
const TagBadge: React.FC<TagBadgeProps & { removeTag: (index: number) => void }> = ({ tag, index, removeTag }) => (
  <div className={styles['video-edit-tag-section__tag-badge']} key={index} data-testid={`video-edit-tag-section__tag-badge-${index}`}>
    <span className={styles['video-edit-tag-section__tag-text']}>{tag}</span>
    <button
      type="button"
      className={styles['video-edit-tag-section__tag-remove-button']}
      onClick={() => removeTag(index)}
      aria-label={`タグ「${tag}」を削除`}
      data-testid={`video-edit-tag-section__tag-remove-button-${index}`}
    >
      <FaTimes />
    </button>
  </div>
);

/**
 * ビデオ編集画面のタグセクションコンポーネント
 * 
 * @param props - VideoEditTagSectionのプロパティ
 * @returns VideoEditTagSectionのReactコンポーネント
 */
const VideoEditTagSection: React.FC<VideoEditTagSectionProps> = ({
  tags,
  setTags,
  inputValue,
  setInputValue,
  isComposing,
  setIsComposing,
  onBulkSetFromBodyPart
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  const {
    inputRef,
    handleInputChange,
    handleCompositionStart,
    handleCompositionEnd,
    handleKeyDown,
    addTag,
    removeTag
  } = useVideoEditTagSection(
    tags,
    setTags,
    inputValue,
    setInputValue,
    isComposing,
    setIsComposing
  );

  // 一括タグ設定ボタンのクリックハンドラー
  const handleBulkSetClick = () => {
    setIsDialogOpen(true);
    onBulkSetFromBodyPart?.();
  };

  // ダイアログを閉じる処理
  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  // 部位選択時の処理
  const handleBodyPartSelect = (part: BodyPart) => {
    // 既存のタグと重複しないように新しいタグを追加
    const newTags = [...tags];
    part.tags.forEach(tag => {
      if (!newTags.includes(tag)) {
        newTags.push(tag);
      }
    });
    setTags(newTags);
  };

  return (
    <>
      <div className={styles['video-edit-tag-section']} data-testid="video-edit-tag-section">
        <div className={styles['video-edit-tag-section__input-container']}>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            placeholder="タグを入力（カンマまたはEnterで追加）"
            className={styles['video-edit-tag-section__input']}
            aria-label="新しいタグを追加"
            data-testid="video-edit-tag-section__input"
          />
          <button
            type="button"
            onClick={handleBulkSetClick}
            className={styles['video-edit-tag-section__bulk-set-button']}
            aria-label="部位から一括設定"
            data-testid="video-edit-tag-section__bulk-set-button"
          >
            部位から一括設定
          </button>
        </div>

        <div className={styles['video-edit-tag-section__list']} data-testid="video-edit-tag-section__list">
          {tags.length > 0 ? (
            tags.map((tag, index) => (
              <TagBadge 
                tag={tag} 
                index={index} 
                key={index} 
                removeTag={removeTag} 
              />
            ))
          ) : (
            <p className={styles['video-edit-tag-section__no-tags-message']} data-testid="video-edit-tag-section__no-tags-message">
              タグが登録されていません。タグを追加すると、動画が検索しやすくなります。
            </p>
          )}
        </div>

        <div className={styles['video-edit-tag-section__instructions']}>
          <p>タグの例: 手術手技, 腹腔鏡, 解剖学, 術中合併症</p>
        </div>
      </div>

      <BulkTagSettingDialog
        isOpen={isDialogOpen}
        onClose={handleDialogClose}
        onSelectBodyPart={handleBodyPartSelect}
      />
    </>
  );
};

export default VideoEditTagSection; 